import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { setupUserConnection, removeUserConnection } from '../components/userConnection';
import { loadStudentModels } from '../utils/instructorUtils';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [studentModels, setStudentModels] = useState(null);
    const connectionUnsubscribeRef = useRef(null);
    const previousUserRef = useRef(null);


    const cleanupUserConnection = async () => {
        try {
            if (connectionUnsubscribeRef.current) {
                connectionUnsubscribeRef.current();
                connectionUnsubscribeRef.current = null;
            }

            if (previousUserRef.current) {
                await removeUserConnection(previousUserRef.current);
                previousUserRef.current = null;
            }
        } catch (error) {
            console.warn('Cleanup error (may be expected):', error);
        }
    };

    const handleLogout = async () => {
        try {
            const auth = getAuth();
            await cleanupUserConnection();
            setStudentModels(null); // Clear student models
            localStorage.removeItem('tutorialCompleted'); // Clear tutorial state
            sessionStorage.clear(); // Clear any session data
            await signOut(auth);
        } catch (error) {
            console.error('Error during logout:', error);
            throw error;
        }
    };


    useEffect(() => {
        const auth = getAuth();
        let authUnsubscribe = null;

        const handleAuthStateChange = async (user) => {
            try {
                if (user) {
                    const db = getFirestore();
                    const userDoc = await getDoc(doc(db, 'users', user.uid));

                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        const userWithData = {
                            ...user,
                            firstName: userData.firstName,
                            lastName: userData.lastName,
                            role: userData.role,
                            classCode: userData.classCode
                        };

                        const unsubscribeConnection = await setupUserConnection(userWithData);
                        connectionUnsubscribeRef.current = unsubscribeConnection;
                        previousUserRef.current = userWithData;

                        setCurrentUser(userWithData);

                        if (userData.role === 'student') {
                            const models = await loadStudentModels(user.uid);
                            if (Object.keys(models).length === 0) {
                                // Force reload if models failed to load
                                window.location.reload();
                            }
                            setStudentModels(models);
                        }

                        setLoading(false); // Only set loading false after all data is loaded
                    } else {
                        console.error('User document not found');
                        setCurrentUser(null);
                        setLoading(false);
                    }
                } else {
                    setCurrentUser(null);
                    setStudentModels(null);
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error in auth state change:', error);
                setCurrentUser(null);
                setStudentModels(null);
                setLoading(false);
            }
        };

        authUnsubscribe = onAuthStateChanged(auth, handleAuthStateChange);

        return () => {
            if (authUnsubscribe) {
                authUnsubscribe();
            }
            cleanupUserConnection();
        };
    }, []);

    const value = {
        currentUser,
        loading,
        studentModels,
        handleLogout
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === null) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}